<template>
  <div>
    <!-- Header -->
    <b-card no-body>
      <b-card-header>
        <b-card-title>{{ $t('services.title') }}</b-card-title>
        <div>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="outline-primary"
            class="mr-1"
            @click="toggleView"
          >
            <feather-icon
              :icon="viewMode === 'tree' ? 'ListIcon' : 'GitBranchIcon'"
              class="mr-50"
            />
            <span class="align-middle">{{ viewMode === 'tree' ? $t('services.actions.list_view') : $t('services.actions.tree_view') }}</span>
          </b-button>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="outline-info"
            class="mr-1"
            :to="{ name: 'service-types-list' }"
          >
            <feather-icon
              icon="LayersIcon"
              class="mr-50"
            />
            <span class="align-middle">المعاملات</span>
          </b-button>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="outline-success"
            class="mr-1"
            :to="{ name: 'service-actions-list' }"
          >
            <feather-icon
              icon="ZapIcon"
              class="mr-50"
            />
            <span class="align-middle">{{ $t('serviceActions.title') }}</span>
          </b-button>
          <b-button
            variant="primary"
            :to="{ name: 'services-create' }"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            <span class="align-middle">{{ $t('services.actions.add_service') }}</span>
          </b-button>
        </div>
      </b-card-header>

      <!-- Filters -->
      <b-card-body>
        <b-row>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="المعاملة"
              label-for="service-type"
            >
              <v-select
                v-model="filters.service_type_id"
                :options="serviceTypeOptions"
                :reduce="option => option.value"
                label="label"
                :placeholder="$t('common.all')"
                @input="fetchData"
              >
                <template #option="option">
                  <span>{{ option.label }}</span>
                  <gl-activity-chip
                    v-if="option.activity"
                    :activity="option.activity"
                    class="ml-50"
                  />
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              :label="$t('services.filters.status')"
              label-for="status"
            >
              <v-select
                v-model="filters.status"
                :options="statusOptions"
                :reduce="option => option.value"
                label="label"
                :placeholder="$t('common.all')"
                @input="fetchData"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              :label="$t('services.filters.priority')"
              label-for="priority"
            >
              <v-select
                v-model="filters.priority"
                :options="priorityOptions"
                :reduce="option => option.value"
                label="label"
                :placeholder="$t('common.all')"
                @input="fetchData"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              :label="$t('services.filters.sort_by')"
              label-for="sort-by"
            >
              <v-select
                v-model="filters.sort_by"
                :options="sortOptions"
                :reduce="option => option.value"
                label="label"
                @input="fetchData"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Tree View -->
    <b-card
      v-if="viewMode === 'tree'"
      no-body
      class="mt-2"
    >
      <b-card-body>
        <div
          v-if="loading"
          class="text-center text-primary my-2"
        >
          <b-spinner class="align-middle mr-1" />
          <strong>{{ $t('common.loading') }}</strong>
        </div>
        <div
          v-else-if="localServiceTree.length === 0"
          class="text-center my-5"
        >
          <feather-icon
            icon="InboxIcon"
            size="48"
            class="text-muted mb-2"
          />
          <p class="text-muted">
            {{ $t('services.messages.no_services_found') }}
          </p>
        </div>
        <div
          v-else
          class="tree-container"
        >
          <draggable
            v-model="localServiceTree"
            :group="{ name: 'services', pull: true, put: true }"
            :animation="200"
            handle=".drag-handle"
            class="list-group root-draggable"
            :class="{ 'has-items': localServiceTree.length > 0, 'empty-root': localServiceTree.length === 0 }"
            @end="handleTreeDragEnd"
            @add="handleRootItemAdded"
          >
            <service-tree-item
              v-for="service in localServiceTree"
              :key="service.id"
              :service="service"
              @edit="editService"
              @delete="confirmDelete"
              @add-child="addChildService"
              @add-action="openActionModal"
              @duplicate="duplicateServiceHandler"
              @move-up="moveServiceUp"
              @move-down="moveServiceDown"
              @view-action="viewAction"
              @edit-action="editAction"
              @duplicate-action="duplicateActionHandler"
              @tree-updated="handleTreeDragEnd"
            />
          </draggable>

          <!-- Root level drop zone indicator when empty -->
          <div
            v-if="localServiceTree.length === 0"
            class="root-drop-zone-indicator"
          >
            <small class="text-muted">
              <feather-icon
                icon="MoveIcon"
                size="16"
                class="mr-25"
              />
              Drop services here to make them root-level parents
            </small>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- List View -->
    <b-card
      v-else
      no-body
      class="mt-2"
    >
      <b-table
        ref="refServicesTable"
        :items="services"
        :fields="tableColumns"
        :busy="loading"
        responsive
        show-empty
        :empty-text="$t('services.messages.no_services_found')"
        class="position-relative"
      >
        <!-- Loading -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-1" />
            <strong>{{ $t('common.loading') }}</strong>
          </div>
        </template>

        <!-- Service Code -->
        <template #cell(service_code)="data">
          <b-link :to="{ name: 'services-edit', params: { id: data.item.id } }">
            <strong>{{ data.item.service_code }}</strong>
          </b-link>
        </template>

        <!-- Path -->
        <template #cell(path)="data">
          <div
            class="text-truncate"
            style="max-width: 250px;"
            :title="data.item.path"
          >
            {{ data.item.path }}
          </div>
        </template>

        <!-- Service Type -->
        <template #cell(service_type)="data">
          <b-badge
            v-if="data.item.service_type"
            :style="{
              backgroundColor: data.item.service_type.color,
              color: '#fff'
            }"
            pill
          >
            {{ data.item.service_type.name }}
          </b-badge>
        </template>

        <!-- Status -->
        <template #cell(status)="data">
          <b-badge
            :variant="getStatusVariant(data.item.status)"
            pill
          >
            {{ formatStatus(data.item.status) }}
          </b-badge>
        </template>

        <!-- Priority -->
        <template #cell(priority)="data">
          <b-badge
            v-if="data.item.priority"
            :variant="getPriorityVariant(data.item.priority)"
            pill
          >
            {{ data.item.priority }}
          </b-badge>
          <span v-else>-</span>
        </template>

        <!-- Actions Count -->
        <template #cell(actions_count)="data">
          <b-badge
            variant="light-primary"
            pill
          >
            {{ data.item.actions_count || 0 }}
          </b-badge>
        </template>

        <!-- Children Count -->
        <template #cell(children_count)="data">
          <b-badge
            variant="light-info"
            pill
          >
            {{ data.item.children_count || 0 }}
          </b-badge>
        </template>

        <!-- Actions -->
        <template #cell(actions)="data">
          <div class="text-nowrap">
            <b-button
              variant="flat-info"
              size="sm"
              :to="{ name: 'services-edit', params: { id: data.item.id } }"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <b-button
              v-b-tooltip.hover
              variant="flat-success"
              size="sm"
              title="Add Child Service"
              @click="addChildService(data.item)"
            >
              <feather-icon icon="PlusIcon" />
            </b-button>
            <b-dropdown
              variant="flat-secondary"
              size="sm"
              no-caret
              right
            >
              <template #button-content>
                <feather-icon icon="MoreVerticalIcon" />
              </template>
              <b-dropdown-item @click="openActionModal(data.item)">
                <feather-icon
                  icon="ZapIcon"
                  size="16"
                  class="mr-50"
                />
                <span>Add Action</span>
              </b-dropdown-item>
              <b-dropdown-item @click="duplicateServiceHandler(data.item)">
                <feather-icon
                  icon="CopyIcon"
                  size="16"
                  class="mr-50"
                />
                <span>Duplicate</span>
              </b-dropdown-item>
            </b-dropdown>
            <b-button
              v-b-tooltip.hover
              variant="flat-danger"
              size="sm"
              title="Delete"
              @click="confirmDelete(data.item)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-card-body
        v-if="viewMode === 'list'"
        class="d-flex justify-content-between align-items-center pt-0"
      >
        <div>
          <span class="text-muted">{{ $t('common.showing') }} {{ meta.from || 0 }} {{ $t('common.to') }} {{ meta.to || 0 }} {{ $t('common.of') }} {{ meta.total || 0 }} {{ $t('common.entries') }}</span>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="meta.total"
          :per-page="filters.per_page"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
          @change="handlePageChange"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </template>
        </b-pagination>
      </b-card-body>
    </b-card>

    <!-- Service Form Modal -->
    <service-form-modal
      :visible="showServiceModal"
      :service="selectedService"
      :parent-service-id="parentServiceId"
      @close="closeServiceModal"
      @saved="handleServiceSaved"
    />

    <!-- Service Action Form Modal -->
    <service-action-form-modal
      :visible="showActionModal"
      :service-id="actionServiceId"
      :available-actions="availableActions"
      @close="closeActionModal"
      @saved="handleActionSaved"
    />

    <!-- View Action Modal -->
    <b-modal
      v-model="showViewActionModal"
      title="View Action Details"
      size="lg"
      ok-only
      ok-title="Close"
    >
      <div v-if="selectedAction">
        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <dl>
              <dt class="text-muted">
                Action Name
              </dt>
              <dd><strong>{{ selectedAction.name }}</strong></dd>
            </dl>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <dl>
              <dt class="text-muted">
                Responsibility Type
              </dt>
              <dd>
                <b-badge
                  :variant="getResponsibilityVariant(selectedAction.default_responsibility_type)"
                  pill
                >
                  {{ formatResponsibilityType(selectedAction.default_responsibility_type) }}
                </b-badge>
              </dd>
            </dl>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <dl>
              <dt class="text-muted">
                Estimated Hours
              </dt>
              <dd>{{ selectedAction.estimated_hours || '-' }}</dd>
            </dl>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <dl>
              <dt class="text-muted">
                Estimated Cost
              </dt>
              <dd>{{ selectedAction.estimated_cost ? `$${selectedAction.estimated_cost}` : '-' }}</dd>
            </dl>
          </b-col>
          <b-col cols="12">
            <dl>
              <dt class="text-muted">
                Description
              </dt>
              <dd>{{ selectedAction.description || '-' }}</dd>
            </dl>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <dl>
              <dt class="text-muted">
                Order
              </dt>
              <dd>{{ selectedAction.order || '-' }}</dd>
            </dl>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <dl>
              <dt class="text-muted">
                Status
              </dt>
              <dd>
                <b-badge
                  :variant="selectedAction.is_active ? 'success' : 'danger'"
                  pill
                >
                  {{ selectedAction.is_active ? 'Active' : 'Inactive' }}
                </b-badge>
              </dd>
            </dl>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BButton,
  BTable,
  BBadge,
  BSpinner,
  BPagination,
  BListGroup,
  BLink,
  BDropdown,
  BDropdownItem,
  VBTooltip,
  BModal,
  BDl,
  BDt,
  BDd,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { mapState, mapActions } from 'vuex'
import draggable from 'vuedraggable'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import GlActivityChip from '@/views/modules/service-templates/service-types/GlActivityChip.vue'
import {
  formatResponsibilityType,
  getResponsibilityVariant,
} from '@/utils/responsibilityType'
import ServiceTreeItem from './components/ServiceTreeItem.vue'
import ServiceFormModal from './components/ServiceFormModal.vue'
import ServiceActionFormModal from './components/ServiceActionFormModal.vue'

export default {
  name: 'ServicesList',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BTable,
    BBadge,
    BSpinner,
    BPagination,
    BListGroup,
    BLink,
    BDropdown,
    BDropdownItem,
    BModal,
    vSelect,
    draggable,
    ServiceTreeItem,
    ServiceFormModal,
    ServiceActionFormModal,
    GlActivityChip,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      viewMode: 'tree', // 'list' or 'tree'
      filters: {
        service_type_id: null,
        status: null,
        priority: null,
        sort_by: 'created_at',
        sort_direction: 'desc',
        per_page: 15,
      },
      currentPage: 1,
      tableColumns: [],
      statusOptions: [],
      priorityOptions: [],
      sortOptions: [],
      serviceTypeOptions: [],
      showServiceModal: false,
      showActionModal: false,
      showViewActionModal: false,
      selectedService: null,
      selectedAction: null,
      parentServiceId: null,
      actionServiceId: null,
      availableActions: [],
      localServiceTree: [], // Local copy for draggable
      useApiTreeFilter: false,
    }
  },
  computed: {
    ...mapState('services', ['services', 'serviceTree', 'meta', 'loading']),
    filteredServiceTree() {
      const tree = Array.isArray(this.serviceTree) ? this.serviceTree : []
      if (!this.filters.service_type_id || this.useApiTreeFilter) {
        return tree
      }
      return this.filterTreeByServiceType(tree, this.filters.service_type_id)
    },
  },
  watch: {
    serviceTree: {
      handler() {
        this.applyTreeFilter()
      },
      deep: true,
    },
    viewMode() {
      this.applyTreeFilter()
    },
  },
  created() {
    this.initializeOptions()
    this.fetchData()
    this.loadServiceTypes()
  },
  methods: {
    ...mapActions('services', [
      'fetchServices',
      'fetchServiceTree',
      'deleteService',
      'duplicateService',
      'reorderServices',
      'fetchService',
    ]),
    ...mapActions('serviceTypes', ['fetchActiveServiceTypes']),
    ...mapActions('serviceActions', ['duplicateServiceAction']),
    initializeOptions() {
      this.tableColumns = [
        { key: 'service_code', label: this.$t('services.table.code'), sortable: true },
        { key: 'name', label: this.$t('services.table.name'), sortable: true },
        { key: 'path', label: this.$t('services.table.path') },
        { key: 'service_type', label: this.$t('services.table.type') },
        { key: 'status', label: this.$t('services.table.status') },
        { key: 'priority', label: this.$t('services.table.priority') },
        { key: 'actions_count', label: this.$t('services.table.actions') },
        { key: 'children_count', label: this.$t('services.table.children') },
        { key: 'actions', label: this.$t('common.actions') },
      ]
      this.statusOptions = [
        { label: this.$t('common.all'), value: null },
        { label: this.$t('services.status.pending'), value: 'pending' },
        { label: this.$t('services.status.in_progress'), value: 'in_progress' },
        { label: this.$t('services.status.completed'), value: 'completed' },
        { label: this.$t('services.status.cancelled'), value: 'cancelled' },
      ]
      this.priorityOptions = [
        { label: this.$t('common.all'), value: null },
        { label: this.$t('services.priority.1_low'), value: 1 },
        { label: this.$t('services.priority.2'), value: 2 },
        { label: this.$t('services.priority.3_medium'), value: 3 },
        { label: this.$t('services.priority.4'), value: 4 },
        { label: this.$t('services.priority.5_high'), value: 5 },
      ]
      this.sortOptions = [
        { label: this.$t('services.filters.created_date'), value: 'created_at' },
        { label: this.$t('services.filters.name'), value: 'name' },
        { label: this.$t('services.filters.priority'), value: 'priority' },
      ]
    },
    applyTreeFilter() {
      this.localServiceTree = this.deepCloneTree(this.filteredServiceTree)
    },
    async fetchData() {
      try {
        if (this.viewMode === 'tree') {
          const hasTypeFilter = Boolean(this.filters.service_type_id)
          const params = hasTypeFilter
            ? { service_type_id: this.filters.service_type_id }
            : {}

          await this.fetchServiceTree(params)

          const hasTreeData = Array.isArray(this.serviceTree) && this.serviceTree.length > 0

          if (hasTypeFilter && hasTreeData) {
            // API already returned filtered tree — don't strip nodes client-side
            this.useApiTreeFilter = true
          } else if (hasTypeFilter) {
            // Fallback: load full tree and filter on client
            this.useApiTreeFilter = false
            await this.fetchServiceTree()
          } else {
            this.useApiTreeFilter = false
          }

          this.applyTreeFilter()
        } else {
          const params = {
            ...this.filters,
            page: this.currentPage,
          }
          await this.fetchServices(params)
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || this.$t('services.messages.load_error'),
          },
        })
      }
    },
    async loadServiceTypes() {
      try {
        const response = await this.fetchActiveServiceTypes()
        this.serviceTypeOptions = [
          { label: this.$t('common.all'), value: null },
          ...response.data.map(st => ({
            label: st.name_ar,
            value: st.id,
            activity: st.effective_gl_activity || null,
          })),
        ]
      } catch (error) {
        console.error('Failed to load service types:', error)
      }
    },
    handlePageChange(page) {
      this.currentPage = page
      this.fetchData()
    },
    toggleView() {
      this.viewMode = this.viewMode === 'list' ? 'tree' : 'list'
      this.fetchData()
    },
    editService(service) {
      this.selectedService = service
      this.parentServiceId = null
      this.showServiceModal = true
    },
    addChildService(service) {
      this.selectedService = null
      this.parentServiceId = service.id
      this.showServiceModal = true
    },
    async openActionModal(service) {
      try {
        // Fetch service details to get available actions
        const response = await this.fetchService(service.id)
        this.actionServiceId = service.id
        this.availableActions = response.data.actions || []
        this.showActionModal = true
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || this.$t('services.messages.load_error'),
          },
        })
      }
    },
    async duplicateServiceHandler(service) {
      this.$swal({
        title: 'Duplicate Service?',
        text: `This will create a copy of "${service.name}"`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, duplicate it!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            await this.duplicateService({ id: service.id })
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Success',
                icon: 'CheckIcon',
                variant: 'success',
                text: 'Service duplicated successfully',
              },
            })
            await this.fetchData()
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: error.response?.data?.message || 'Failed to duplicate service',
              },
            })
          }
        }
      })
    },
    async moveServiceUp(service) {
      // Implement move up logic
      await this.reorderService(service, -1)
    },
    async moveServiceDown(service) {
      // Implement move down logic
      await this.reorderService(service, 1)
    },
    async reorderService(service, direction) {
      try {
        // Get siblings
        const siblings = this.viewMode === 'tree'
          ? this.getSiblings(service, this.localServiceTree)
          : this.services.filter(s => s.parent_service_id === service.parent_service_id)

        const currentIndex = siblings.findIndex(s => s.id === service.id)
        const newIndex = currentIndex + direction

        if (newIndex < 0 || newIndex >= siblings.length) {
          return // Can't move further
        }

        // Swap orders
        const reorderData = siblings.map((s, index) => {
          let order = index
          if (index === currentIndex) {
            order = newIndex
          } else if (index === newIndex) {
            order = currentIndex
          }
          return { id: s.id, order }
        })

        await this.reorderServices(reorderData)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Service order updated',
          },
        })

        await this.fetchData()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || 'Failed to reorder service',
          },
        })
      }
    },
    async handleTreeDragEnd() {
      try {
        // Update parent_service_id for moved items based on new tree structure
        this.updateTreeParentIds(this.localServiceTree, null)

        // Send the full tree structure to reorderServices
        await this.reorderServices(this.localServiceTree)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Service order updated',
          },
        })

        // Refresh data to get updated tree from server
        await this.fetchData()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || 'Failed to reorder services',
          },
        })
        // Refresh to revert changes
        await this.fetchData()
      }
    },
    handleRootItemAdded() {
      // Ensure items added to root have parent_service_id set to null
      this.localServiceTree.forEach(service => {
        if (service.parent_service_id !== null && service.parent_service_id !== undefined) {
          service.parent_service_id = null
        }
      })
      this.handleTreeDragEnd()
    },
    deepCloneTree(tree) {
      if (!Array.isArray(tree)) return []
      return tree.map(service => ({
        ...service,
        children: service.children && Array.isArray(service.children)
          ? this.deepCloneTree(service.children)
          : [],
      }))
    },
    nodeMatchesServiceType(node, serviceTypeId, inheritedTypeId = null) {
      const rawType = node.service_type?.id
        ?? node.service_type_id
        ?? (typeof node.service_type === 'number' ? node.service_type : null)
        ?? inheritedTypeId
      return rawType != null && Number(rawType) === Number(serviceTypeId)
    },
    filterTreeByServiceType(nodes, serviceTypeId, inheritedTypeId = null) {
      if (!Array.isArray(nodes) || !serviceTypeId) return nodes || []

      return nodes
        .map(node => {
          const effectiveTypeId = node.service_type?.id
            ?? node.service_type_id
            ?? (typeof node.service_type === 'number' ? node.service_type : null)
            ?? inheritedTypeId
          const matches = this.nodeMatchesServiceType(node, serviceTypeId, inheritedTypeId)

          if (matches) {
            return {
              ...node,
              children: node.children || [],
            }
          }

          const children = this.filterTreeByServiceType(node.children || [], serviceTypeId, effectiveTypeId)
          if (children.length > 0) {
            return { ...node, children }
          }

          return null
        })
        .filter(Boolean)
    },
    updateTreeParentIds(tree, parentId) {
      if (!Array.isArray(tree)) return

      tree.forEach((service, index) => {
        // Update parent_service_id
        service.parent_service_id = parentId

        // Recursively update children
        if (service.children && Array.isArray(service.children) && service.children.length > 0) {
          this.updateTreeParentIds(service.children, service.id)
        }
      })
    },
    getSiblings(service, tree) {
      // Recursive function to find siblings
      for (const node of tree) {
        if (node.id === service.parent_service_id) {
          return node.children || []
        }
        if (node.children && node.children.length > 0) {
          const result = this.getSiblings(service, node.children)
          if (result) return result
        }
      }
      // If no parent found, it's a root service
      return tree.filter(s => !s.parent_service_id)
    },
    closeServiceModal() {
      this.showServiceModal = false
      this.selectedService = null
      this.parentServiceId = null
    },
    closeActionModal() {
      this.showActionModal = false
      this.actionServiceId = null
      this.availableActions = []
    },
    async handleServiceSaved() {
      await this.fetchData()
    },
    async handleActionSaved() {
      await this.fetchData()
    },
    viewAction(action) {
      this.selectedAction = action
      this.showViewActionModal = true
    },
    editAction(action) {
      this.$router.push({ name: 'service-actions-edit', params: { id: action.id } })
    },
    async duplicateActionHandler(action) {
      this.$swal({
        title: 'Duplicate Action?',
        text: `This will create a copy of "${action.name}"`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, duplicate it!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            await this.duplicateServiceAction({ id: action.id })
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('common.success'),
                icon: 'CheckIcon',
                variant: 'success',
                text: 'Action duplicated successfully',
              },
            })
            await this.fetchData()
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('common.error'),
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: error.response?.data?.message || 'Failed to duplicate action',
              },
            })
          }
        }
      })
    },
    getResponsibilityVariant(type) {
      return getResponsibilityVariant(type)
    },
    formatResponsibilityType(type) {
      return formatResponsibilityType(type)
    },
    confirmDelete(service) {
      this.$swal({
        title: this.$t('services.messages.confirm_delete'),
        text: this.$t('services.messages.confirm_delete_text'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('services.messages.yes_delete'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            await this.deleteService(service.id)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('common.success'),
                icon: 'CheckIcon',
                variant: 'success',
                text: this.$t('services.messages.delete_success'),
              },
            })
            await this.fetchData()
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('common.error'),
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: error.response?.data?.message || this.$t('services.messages.delete_error'),
              },
            })
          }
        }
      })
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        in_progress: 'info',
        completed: 'success',
        cancelled: 'danger',
      }
      return variants[status] || 'secondary'
    },
    formatStatus(status) {
      return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'
    },
    getPriorityVariant(priority) {
      if (priority <= 2) return 'success'
      if (priority === 3) return 'warning'
      return 'danger'
    },
  },
}
</script>

<style scoped>
.tree-container {
  position: relative;
}

.root-draggable {
  min-height: 3rem;
  padding: 0.5rem;
  border: 2px dashed transparent;
  border-radius: 4px;
  transition: all 0.2s;
}

.root-draggable.has-items {
  min-height: 0;
  padding: 0;
  border: none;
}

.root-draggable.empty-root {
  border-color: #e0e0e0;
  background-color: #fafafa;
}

.root-draggable.sortable-drag-over {
  border-color: #7367f0;
  background-color: #f0f4ff;
  border-width: 2px;
}

.root-drop-zone-indicator {
  padding: 1rem;
  border: 2px dashed #d0d0d0;
  border-radius: 4px;
  text-align: center;
  background-color: #fafafa;
  transition: all 0.2s;
  min-height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 0.5rem;
  pointer-events: none;
}

.root-drop-zone-indicator:hover {
  border-color: #7367f0;
  background-color: #f0f4ff;
}

/* Visual feedback when dragging over root drop zone */
.root-draggable.sortable-drag-over + .root-drop-zone-indicator {
  border-color: #7367f0;
  background-color: #f0f4ff;
  border-width: 2px;
}
</style>
